import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { ArrowRight, Sparkles } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Typewriter Effect Component
const TypewriterText = ({ text, duration = 2000, delay = 0, onComplete }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  
  useEffect(() => {
    if (!text) return;
    
    // Wait for delay before starting
    const delayTimer = setTimeout(() => {
      setHasStarted(true);
      
      const chars = text.split('');
      const charDelay = duration / chars.length;
      let currentIndex = 0;
      
      const timer = setInterval(() => {
        if (currentIndex < chars.length) {
          setDisplayedText(text.slice(0, currentIndex + 1));
          currentIndex++;
        } else {
          clearInterval(timer);
          setIsComplete(true);
          onComplete?.();
        }
      }, charDelay);
      
      return () => clearInterval(timer);
    }, delay);
    
    return () => clearTimeout(delayTimer);
  }, [text, duration, delay, onComplete]);
  
  return (
    <span>
      {displayedText}
      {hasStarted && !isComplete && <span className="animate-pulse">|</span>}
    </span>
  );
};

// Animated Counter Component
const AnimatedCounter = ({ stat }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const counterRef = useRef(null);

  useEffect(() => {
    // Start animation immediately when component mounts
    if (hasStarted) return;
    
    const numericValue = stat.numeric_value;
    const animationType = stat.type;
    
    // For static type, just show the value
    if (animationType === 'static' || !numericValue) {
      setDisplayValue(numericValue || 0);
      return;
    }

    setHasStarted(true);
    
    const duration = 2000; // 2 seconds
    const startTime = performance.now();
    const isCountDown = animationType === 'count_down';
    
    // For count_down: start at 3x the value, end at target
    // For count_up: start at 0, end at target
    const startVal = isCountDown ? numericValue * 3 : 0;
    const endVal = numericValue;

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function - easeOutExpo for snappy feel
      const easeOutExpo = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      
      const currentVal = Math.round(startVal + (endVal - startVal) * easeOutExpo);
      setDisplayValue(currentVal);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [stat, hasStarted]);

  // Format the display value
  const formatValue = () => {
    const animationType = stat.type;
    
    if (animationType === 'static') {
      return stat.value;
    }
    
    if (animationType === 'count_up') {
      return `${displayValue.toLocaleString()}+`;
    }
    
    if (animationType === 'count_down') {
      return `${displayValue} min`;
    }
    
    return stat.value;
  };

  return (
    <div ref={counterRef} className="text-center">
      <p className="text-2xl font-bold text-white tabular-nums">{formatValue()}</p>
      <p className="text-xs text-slate-500">{stat.label}</p>
    </div>
  );
};

const HeroSection = ({ onGetStarted }) => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSettings(response.data);
      } catch (error) {
        console.error('Failed to fetch settings:', error);
        // Use default values if API fails
        setSettings({
          hero_headline_before: 'Protect what',
          hero_headline_italic: 'matters most',
          hero_headline_after: ', with ease.',
          hero_subtitle: 'WillKart.ai is your trusted companion for creating legally valid wills in minutes. Secure your legacy and give your loved ones peace of mind.',
          hero_cta_primary: 'Get Started',
          hero_cta_secondary: 'Learn More',
          trust_text: 'Trusted by families across India',
          trust_stats: [
            { id: '1', value: '10,000+', label: 'Wills Created', type: 'count_up', numeric_value: 10000 },
            { id: '2', value: '100%', label: 'Legally Valid', type: 'static', numeric_value: 100 },
            { id: '3', value: '5 min', label: 'Average Time', type: 'count_down', numeric_value: 5 }
          ]
        });
      }
    };

    fetchSettings();
  }, []);

  const scrollToSection = (id) => {
    const element = document.querySelector(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (!settings) {
    return (
      <section className="relative min-h-screen flex items-center justify-center bg-slate-950">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-400"></div>
      </section>
    );
  }

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-slate-950">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-slate-700/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-amber-500/5 to-transparent rounded-full" />
      </div>

      {/* Subtle Grid Pattern */}
      <div 
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}
      />

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-24">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/50 border border-slate-700/50 mb-8 backdrop-blur-sm">
          <Sparkles className="w-4 h-4 text-amber-400" />
          <span className="text-sm text-slate-300">AI-Powered Will Creation</span>
        </div>

        {/* Headline with Typewriter Effect - Sequential Animation */}
        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-8 min-h-[1.2em] sm:min-h-[2.4em]">
          <TypewriterText 
            text={`${settings.hero_headline_before} `}
            duration={1000}
            delay={0}
          />
          <em className="italic text-amber-400 font-serif not-italic bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent">
            <TypewriterText 
              text={settings.hero_headline_italic}
              duration={1200}
              delay={1000}
            />
          </em>
          <br className="hidden sm:block" />
          <TypewriterText 
            text={settings.hero_headline_after}
            duration={1000}
            delay={2200}
          />
        </h1>

        {/* Subtitle */}
        <p className="text-lg sm:text-xl text-slate-400 max-w-3xl mx-auto mb-12 leading-relaxed">
          {settings.hero_subtitle}
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button
            size="lg"
            className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold px-8 py-6 text-lg rounded-xl shadow-lg shadow-amber-500/25 hover:shadow-amber-400/30 transition-all duration-300 group"
            onClick={onGetStarted}
            data-testid="hero-get-started-btn"
          >
            {settings.hero_cta_primary}
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white px-8 py-6 text-lg rounded-xl transition-all duration-300"
            onClick={() => scrollToSection('#features')}
          >
            {settings.hero_cta_secondary}
          </Button>
        </div>

        {/* Trust Indicators */}
        <div className="mt-16 pt-8 border-t border-slate-800/50">
          <p className="text-sm text-slate-500 mb-4">{settings.trust_text}</p>
          <div className="flex items-center justify-center gap-8 text-slate-400">
            {(settings.trust_stats || []).map((stat, index) => (
              <React.Fragment key={stat.id || index}>
                {index > 0 && <div className="w-px h-10 bg-slate-700" />}
                <AnimatedCounter stat={stat} />
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
